import { useState } from 'react'

/**
 * Filtrarea listelor de sampling după tipul acțiunii, și apoi după o acțiune anume.
 *
 * Cerută de Victor după ce a văzut listele amestecate: o predare de mostre într-o farmacie și o
 * înscriere la tombolă ajung în același tabel, dar nu se folosesc la fel. Una e document
 * justificativ, cealaltă e un nume pentru o extragere.
 *
 * Cele două filtre sunt în cascadă, nu alături: alegi întâi tipul, apoi acțiunea — lista de
 * acțiuni se scurtează singură, în loc să afișeze tot ce a existat vreodată.
 *
 * Peste ele, o CĂUTARE. Lista crește cu fiecare farmacie vizitată — la o sută de acțiuni, un
 * `<select>` nativ te obligă să derulezi și nu se poate scrie în el (Alexandru, 29.09.2026).
 * Nu e un combobox adevărat, cu navigare din tastatură: e un câmp care scurtează opțiunile.
 * Diferența costă 150 de linii de comportament pentru un câștig mic.
 */

export type TipSampling = 'toate' | 'farmacie' | 'eveniment' | 'tombola' | 'promo'

const ETICHETE: { valoare: TipSampling; text: string }[] = [
  { valoare: 'toate', text: 'Toate' },
  { valoare: 'farmacie', text: 'Farmacii' },
  { valoare: 'eveniment', text: 'Evenimente' },
  { valoare: 'tombola', text: 'Tombole' },
  { valoare: 'promo', text: 'Promoționale' },
]

interface ActiuneScurta {
  id: number
  denumire: string
  tip: string
}

interface Props {
  valoare: TipSampling
  onChange: (v: TipSampling) => void
  actiuni: ActiuneScurta[]
  actiuneId: number | null
  onActiune: (id: number | null) => void
}

export function FiltreTip({ valoare, onChange, actiuni, actiuneId, onActiune }: Props) {
  const [cauta, setCauta] = useState('')
  const dupaTip = actiuni.filter((a) => valoare === 'toate' || a.tip === valoare)
  const termen = cauta.trim().toLowerCase()
  const potrivite = termen
    ? dupaTip.filter((a) => a.denumire.toLowerCase().includes(termen))
    : dupaTip

  /*
    Acțiunea ALEASĂ rămâne în listă chiar dacă nu se potrivește căutării. Altfel, scriind în
    câmp după ce ai ales, selectul ar rămâne fără opțiunea curentă și ar sări pe „Toate
    acțiunile" — adică ar schimba filtrul de sub mâna omului.
  */
  const aleasa = actiuneId != null ? actiuni.find((a) => a.id === actiuneId) : undefined
  const optiuni =
    aleasa && !potrivite.some((a) => a.id === aleasa.id) ? [aleasa, ...potrivite] : potrivite

  return (
    <div className="flex flex-wrap items-center gap-2">
      {ETICHETE.map((e) => (
        <button
          key={e.valoare}
          onClick={() => onChange(e.valoare)}
          className={`rounded-full px-3 py-1 text-sm ${
            valoare === e.valoare ? 'bg-b4l-blue text-white' : 'bg-b4l-bg text-b4l-text-muted'
          }`}
        >
          {e.text}
        </button>
      ))}

      <input
        value={cauta}
        onChange={(e) => setCauta(e.target.value)}
        placeholder="Caută acțiunea…"
        className="w-48 rounded-lg border border-b4l-border px-2 py-1 text-sm"
      />

      <select
        value={actiuneId ?? ''}
        onChange={(e) => onActiune(e.target.value ? Number(e.target.value) : null)}
        className="max-w-[22rem] rounded-lg border border-b4l-border bg-white px-2 py-1 text-sm"
      >
        <option value="">Toate acțiunile</option>
        {optiuni.map((a) => (
          <option key={a.id} value={a.id}>
            {a.denumire}
          </option>
        ))}
      </select>

      {/* Cât a mai rămas din listă: fără cifra asta, o căutare fără rezultate arată ca o eroare. */}
      {termen && (
        <span className="text-xs text-b4l-text-muted">
          {potrivite.length === 0
            ? 'nicio acțiune pe căutarea asta'
            : `${potrivite.length} din ${dupaTip.length}`}
        </span>
      )}
    </div>
  )
}
